(() => {
  if (window.__KP_CROSS_SELL_INITIALIZED__) return;
  window.__KP_CROSS_SELL_INITIALIZED__ = true;

  const money = (cents) => {
    const locale = document.documentElement.lang || 'da-DK';
    const currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'DKK';
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(Number(cents || 0) / 100);
    } catch (_) {
      return `${(Number(cents || 0) / 100).toFixed(2)} ${currency}`;
    }
  };

  const getButtonLabel = (form) => {
    const button = form.querySelector('.product-form__submit[name="add"]');
    if (!button) return null;
    const label = button.querySelector('span:not(.sold-out-message)');
    if (!label) return null;
    if (!label.dataset.kpOriginalLabel) label.dataset.kpOriginalLabel = label.textContent.trim();
    return { button, label };
  };

  const updateBox = (box) => {
    if (!box) return;
    const form = box.closest('form');
    if (!form) return;

    const checked = [...box.querySelectorAll('.kp-cross-sell__checkbox:checked')];
    box.querySelectorAll('.kp-cross-sell__row').forEach((row) => {
      const input = row.querySelector('.kp-cross-sell__checkbox');
      row.classList.toggle('is-selected', Boolean(input && input.checked));
    });

    const summary = box.querySelector('[data-kp-cross-sell-summary]');
    const total = checked.reduce((sum, input) => sum + Number(input.dataset.priceCents || 0), 0);
    if (summary) {
      summary.hidden = checked.length === 0;
      summary.textContent = checked.length ? `Tilvalg: ${money(total)}` : '';
    }

    const buttonParts = getButtonLabel(form);
    if (buttonParts) {
      buttonParts.label.textContent = checked.length ? 'Læg valgte dele i kurv' : buttonParts.label.dataset.kpOriginalLabel;
    }

    const dynamicCheckout = form.querySelector('.shopify-payment-button');
    if (dynamicCheckout) dynamicCheckout.hidden = checked.length > 0;
  };

  const cartRoot = () => {
    if (window.Shopify && Shopify.routes && Shopify.routes.root) return Shopify.routes.root;
    return '/';
  };

  const fetchCartSnapshot = async () => {
    const response = await fetch(`${cartRoot()}cart.js`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error('Kunne ikke hente den opdaterede kurv.');
    return response.json();
  };

  const sameVariant = (line, id) => String(line && (line.variant_id || line.id)) === String(id);

  const buildDisplayItems = (cartSnapshot, requestedItems, addResponseItems = []) => {
    const cartLines = cartSnapshot && Array.isArray(cartSnapshot.items) ? cartSnapshot.items : [];
    const responseLines = Array.isArray(addResponseItems) ? addResponseItems : [];

    return requestedItems.map((requested) => {
      const line = cartLines.find((candidate) => sameVariant(candidate, requested.id));
      const fallback = responseLines.find((candidate) => sameVariant(candidate, requested.id));
      const source = line || fallback || {};

      return {
        id: requested.id,
        addedQuantity: Math.max(1, Number(requested.quantity || 1)),
        title: source.product_title || source.title || 'Produkt',
        variantTitle: source.variant_title && source.variant_title !== 'Default Title' ? source.variant_title : '',
        url: source.url || '',
        image:
          (source.featured_image && (source.featured_image.url || source.featured_image.src)) ||
          source.image ||
          '',
        imageAlt:
          (source.featured_image && source.featured_image.alt) ||
          source.product_title ||
          source.title ||
          '',
        priceCents: Number(
          line
            ? (line.final_price ?? line.discounted_price ?? line.price ?? 0)
            : (fallback && fallback.price != null ? fallback.price : 0)
        ),
      };
    });
  };

  const ensureMultiAddStyles = () => {
    if (document.getElementById('kp-cart-notification-multi-style')) return;

    const style = document.createElement('style');
    style.id = 'kp-cart-notification-multi-style';
    style.textContent = `
      #cart-notification-product.kp-cart-notification-product--multi {
        display: block !important;
        width: 100% !important;
        max-width: none !important;
        padding: 1.2rem 0 1.8rem !important;
        margin: 0 !important;
      }
      #cart-notification-product.kp-cart-notification-product--multi .kp-cart-notification-list {
        display: flex !important;
        flex-direction: column !important;
        width: 100% !important;
        max-width: none !important;
        margin: 0 !important;
        padding: 0 !important;
      }
      #cart-notification-product.kp-cart-notification-product--multi .kp-cart-notification-item {
        display: grid !important;
        grid-template-columns: 6.8rem minmax(0, 1fr) !important;
        gap: 1.2rem !important;
        align-items: center !important;
        width: 100% !important;
        min-width: 0 !important;
        padding: 1.15rem 0 !important;
        margin: 0 !important;
      }
      #cart-notification-product.kp-cart-notification-product--multi .kp-cart-notification-item + .kp-cart-notification-item {
        border-top: 0.1rem solid rgba(var(--color-foreground), 0.12);
      }
      #cart-notification-product.kp-cart-notification-product--multi .kp-cart-notification-media {
        width: 6.8rem !important;
        height: 6.8rem !important;
        min-width: 6.8rem !important;
        border: 0.1rem solid rgba(var(--color-foreground), 0.12);
        background: rgb(var(--color-background));
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        overflow: hidden !important;
      }
      #cart-notification-product.kp-cart-notification-product--multi .kp-cart-notification-media img {
        display: block !important;
        width: 100% !important;
        height: 100% !important;
        object-fit: contain !important;
        padding: 0.35rem !important;
      }
      #cart-notification-product.kp-cart-notification-product--multi .kp-cart-notification-placeholder {
        width: 100% !important;
        height: 100% !important;
        background: rgba(var(--color-foreground), 0.035);
      }
      #cart-notification-product.kp-cart-notification-product--multi .kp-cart-notification-body {
        min-width: 0 !important;
        width: 100% !important;
      }
      #cart-notification-product.kp-cart-notification-product--multi .kp-cart-notification-title {
        display: block !important;
        margin: 0 0 0.45rem !important;
        padding: 0 !important;
        color: rgb(var(--color-foreground));
        font-size: 1.45rem !important;
        line-height: 1.35 !important;
        font-weight: 600 !important;
        text-decoration: none !important;
        word-break: normal !important;
        overflow-wrap: break-word !important;
        hyphens: none !important;
      }
      #cart-notification-product.kp-cart-notification-product--multi a.kp-cart-notification-title:hover {
        text-decoration: underline !important;
        text-underline-offset: 0.2rem;
      }
      #cart-notification-product.kp-cart-notification-product--multi .kp-cart-notification-variant {
        margin: -0.15rem 0 0.45rem !important;
        color: rgba(var(--color-foreground), 0.65);
        font-size: 1.2rem !important;
        line-height: 1.35 !important;
      }
      #cart-notification-product.kp-cart-notification-product--multi .kp-cart-notification-meta {
        display: flex !important;
        align-items: baseline !important;
        justify-content: space-between !important;
        gap: 1rem !important;
        width: 100% !important;
        margin-top: 0.35rem !important;
      }
      #cart-notification-product.kp-cart-notification-product--multi .kp-cart-notification-qty {
        color: rgba(var(--color-foreground), 0.65);
        font-size: 1.2rem !important;
        line-height: 1.3 !important;
        white-space: nowrap !important;
      }
      #cart-notification-product.kp-cart-notification-product--multi .kp-cart-notification-price {
        color: rgb(var(--color-foreground));
        font-size: 1.45rem !important;
        line-height: 1.25 !important;
        font-weight: 700 !important;
        white-space: nowrap !important;
        margin-left: auto !important;
      }
      @media screen and (max-width: 749px) {
        #cart-notification-product.kp-cart-notification-product--multi .kp-cart-notification-item {
          grid-template-columns: 6.2rem minmax(0, 1fr) !important;
          gap: 1rem !important;
        }
        #cart-notification-product.kp-cart-notification-product--multi .kp-cart-notification-media {
          width: 6.2rem !important;
          height: 6.2rem !important;
          min-width: 6.2rem !important;
        }
      }
    `;
    document.head.appendChild(style);
  };

  const renderMultiAddNotification = (cart, displayItems) => {
    if (!cart || !displayItems.length) return;
    const target = cart.querySelector('#cart-notification-product');
    if (!target) return;

    ensureMultiAddStyles();
    target.classList.add('kp-cart-notification-product--multi');

    const list = document.createElement('div');
    list.className = 'kp-cart-notification-list';

    displayItems.forEach((item) => {
      const row = document.createElement('div');
      row.className = 'kp-cart-notification-item';

      const media = document.createElement(item.url ? 'a' : 'div');
      media.className = 'kp-cart-notification-media';
      if (item.url) media.href = item.url;

      if (item.image) {
        const image = document.createElement('img');
        image.src = item.image;
        image.alt = item.imageAlt || '';
        image.loading = 'eager';
        image.width = 136;
        image.height = 136;
        media.appendChild(image);
      } else {
        const placeholder = document.createElement('span');
        placeholder.className = 'kp-cart-notification-placeholder';
        placeholder.setAttribute('aria-hidden', 'true');
        media.appendChild(placeholder);
      }

      const body = document.createElement('div');
      body.className = 'kp-cart-notification-body';

      const title = document.createElement(item.url ? 'a' : 'div');
      title.className = 'kp-cart-notification-title';
      title.textContent = item.title;
      if (item.url) title.href = item.url;
      body.appendChild(title);

      if (item.variantTitle) {
        const variant = document.createElement('p');
        variant.className = 'kp-cart-notification-variant';
        variant.textContent = item.variantTitle;
        body.appendChild(variant);
      }

      const meta = document.createElement('div');
      meta.className = 'kp-cart-notification-meta';

      const qty = document.createElement('span');
      qty.className = 'kp-cart-notification-qty';
      qty.textContent = item.addedQuantity === 1 ? '1 stk. tilføjet' : `${item.addedQuantity} stk. tilføjet`;
      meta.appendChild(qty);

      if (item.priceCents > 0) {
        const price = document.createElement('strong');
        price.className = 'kp-cart-notification-price';
        price.textContent = money(item.priceCents * item.addedQuantity);
        meta.appendChild(price);
      }

      body.appendChild(meta);
      row.append(media, body);
      list.appendChild(row);
    });

    target.replaceChildren(list);
  };

  const restoreStandardNotificationMode = (cart) => {
    const target = cart && cart.querySelector('#cart-notification-product');
    if (target) target.classList.remove('kp-cart-notification-product--multi');
  };

  document.addEventListener('change', (event) => {
    if (!event.target.classList || !event.target.classList.contains('kp-cross-sell__checkbox')) return;
    updateBox(event.target.closest('[data-kp-cross-sell]'));
  });

  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form.dataset.type !== 'add-to-cart-form') return;

    const box = form.querySelector('[data-kp-cross-sell]');
    if (!box) return;

    const checked = [...box.querySelectorAll('.kp-cross-sell__checkbox:checked')];
    if (!checked.length) {
      const normalCart = document.querySelector('cart-notification');
      restoreStandardNotificationMode(normalCart);
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    const productForm = form.closest('product-form');
    const buttonParts = getButtonLabel(form);
    const submitButton = buttonParts && buttonParts.button;
    if (!submitButton || submitButton.getAttribute('aria-disabled') === 'true' || submitButton.disabled) return;

    const spinner = submitButton.querySelector('.loading__spinner');
    const errorWrapper = productForm && productForm.querySelector('.product-form__error-message-wrapper');
    const errorText = errorWrapper && errorWrapper.querySelector('.product-form__error-message');

    const setError = (message) => {
      if (!errorWrapper || !errorText) return;
      errorText.textContent = message || '';
      errorWrapper.toggleAttribute('hidden', !message);
    };

    setError('');
    submitButton.setAttribute('aria-disabled', 'true');
    submitButton.classList.add('loading');
    if (spinner) spinner.classList.remove('hidden');

    try {
      const formData = new FormData(form);
      const mainVariantId = formData.get('id');
      if (!mainVariantId) throw new Error('Produktvarianten kunne ikke bestemmes.');

      const mainItem = {
        id: Number(mainVariantId),
        quantity: Math.max(1, Number(formData.get('quantity') || 1)),
      };

      const sellingPlan = formData.get('selling_plan');
      if (sellingPlan) mainItem.selling_plan = Number(sellingPlan);

      const properties = {};
      for (const [key, value] of formData.entries()) {
        const match = key.match(/^properties\[(.*)\]$/);
        if (match && typeof value === 'string' && value !== '') properties[match[1]] = value;
      }
      if (Object.keys(properties).length) mainItem.properties = properties;

      const seen = new Set([String(mainVariantId)]);
      const items = [mainItem];
      checked.forEach((input) => {
        const id = String(input.value);
        if (!id || seen.has(id)) return;
        seen.add(id);
        items.push({ id: Number(id), quantity: 1 });
      });

      const requestedItems = items.map((item) => ({ id: item.id, quantity: item.quantity }));
      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const payload = { items };

      if (cart && typeof cart.getSectionsToRender === 'function') {
        payload.sections = cart.getSectionsToRender().map((section) => section.id);
        payload.sections_url = `${window.location.pathname}${window.location.search}`;
        if (typeof cart.setActiveElement === 'function') cart.setActiveElement(document.activeElement);
      }

      const cartAddUrl = typeof routes !== 'undefined' && routes.cart_add_url
        ? routes.cart_add_url
        : `${cartRoot()}cart/add.js`;

      const response = await fetch(cartAddUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'same-origin',
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || 'Varerne kunne ikke lægges i kurven.');
      }

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'kp-cross-sell',
          productVariantId: String(mainVariantId),
          cartData: data,
        });
      }

      let cartSnapshot = null;
      try {
        cartSnapshot = await fetchCartSnapshot();
      } catch (snapshotError) {
        console.warn('KaffeParts cart snapshot fallback:', snapshotError);
      }
      const displayItems = buildDisplayItems(cartSnapshot, requestedItems, data.items);

      if (cart && data.sections) {
        if (!data.key && Array.isArray(data.items)) {
          const mainAddedItem = data.items.find((item) => String(item.variant_id) === String(mainVariantId));
          data.key = (mainAddedItem && mainAddedItem.key) || (data.items[0] && data.items[0].key);
        }

        if (data.key && typeof cart.renderContents === 'function') {
          cart.renderContents(data);
          renderMultiAddNotification(cart, displayItems);
        } else {
          window.location = window.routes ? window.routes.cart_url : '/cart';
        }
      } else {
        window.location = window.routes ? window.routes.cart_url : '/cart';
      }

      checked.forEach((input) => { input.checked = false; });
      updateBox(box);
    } catch (error) {
      console.error('KaffeParts cross-sell add error:', error);
      setError(error && error.message ? error.message : 'Varerne kunne ikke lægges i kurven.');
    } finally {
      submitButton.classList.remove('loading');
      submitButton.removeAttribute('aria-disabled');
      if (spinner) spinner.classList.add('hidden');
    }
  }, true);

  document.querySelectorAll('[data-kp-cross-sell]').forEach(updateBox);
})();
